import { escapeForRegExpLiteral, notEmpty } from "@de-xima/xima-common-js-lang";

interface ISplitResult {
  split: string[];
  incompletePlaceholder: boolean;
  hasPlaceholder: boolean;
}

/**
 * This is an adoption of the algorithm in
 * `de.xima.fc.gui.common.converter.PlaceholderStringListConverter`. Splits a
 * string by the given delimiters, but not if the delimiters occur within a
 * placeholder.
 * @param value String to split
 * @param delimiters Delimiters by which to split.
 * @param allowIncompletePlaceholder Whether an incomplete placeholder
 * (unclosed) is allowed at the end of the string.
 * @returns The given string, split by the delimiters. If placeholders occur in
 * the string, they are not split, even a delimiter occurs within their content.
 */
function split(value: string, delimiters: string, allowIncompletePlaceholder: boolean): ISplitResult {
  const PatternPlaceholderAll = allowIncompletePlaceholder ? /\[%[^%]+(%\]|%$|$)/g : /\[%[^%]+%\]/g;
  const result = [] as string[];
  let previous = 0;
  let match: RegExpExecArray | null = null;
  let incompletePlaceholder = false;
  let hasPlaceholder = false;
  let endsOnDelimiter = false;
  match = PatternPlaceholderAll.exec(value);
  while (match !== null) {
    endsOnDelimiter = addLiterals(result, value.substring(previous, match.index), delimiters);
    if (endsOnDelimiter || result.length === 0) {
      result.push(match[0] ?? "");
    } else {
      result[result.length - 1] = (result[result.length - 1] ?? "") + (match[0] ?? "");
    }
    previous = PatternPlaceholderAll.lastIndex;
    incompletePlaceholder = match.length > 1 && match[1]?.length !== 2;
    hasPlaceholder = true;
    match = PatternPlaceholderAll.exec(value);
  }
  if (previous < value.length) {
    addLiterals(result, value.substring(previous), delimiters);
  }
  return {
    hasPlaceholder: hasPlaceholder,
    incompletePlaceholder: incompletePlaceholder,
    split: result,
  };
}

/**
 * Splits the literal by the delimiters and add them to the result.
 * @param result Result array to which to push items.
 * @param literal Literal to add.
 * @param delimiters Delimiter characters.
 * @returns Whether the literal ends on a delimiter.
 */
function addLiterals(result: string[], literal: string, delimiters: string): boolean {
  const regexp = new RegExp(`[${escapeForRegExpLiteral(delimiters)}]`, "g");
  const startOnDelimiter = startsWithAny(literal, delimiters);
  const endsOnDelimiter = endsWithAny(literal, delimiters);
  const split = literal.split(regexp);
  if (split.length === 0) {
    return endsOnDelimiter;
  }
  const first = split[0] ?? "";
  if (!startOnDelimiter && result.length > 0) {
    result[result.length - 1] = (result[result.length - 1] ?? "") + first;
  } else if (notEmpty(first)) {
    result.push(first);
  }
  for (let i = 1; i < split.length; i += 1) {
    const item = split[i] ?? "";
    if (notEmpty(item)) {
      result.push(item);
    }
  }
  return endsOnDelimiter;
}

/**
 * @param string String to analyze.
 * @param delimiters One or more delimiters.
 * @returns `true` if the string starts with any of the given characters.
 */
function startsWithAny(string: string, delimiters: string) {
  if (notEmpty(string)) {
    const firstChar = string.charAt(0);
    for (let i = 0; i < delimiters.length; i += 1) {
      if (firstChar === delimiters.charAt(i)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * @param string String to analyze.
 * @param delimiters One or more delimiters.
 * @returns `true` if the string ends with any of the given characters.
 */
function endsWithAny(string: string, delimiters: string) {
  if (notEmpty(string)) {
    const lastChar = string.charAt(string.length - 1);
    for (let i = 0; i < delimiters.length; i += 1) {
      if (lastChar === delimiters.charAt(i)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Adjusts the tagify component so that placeholders are not split.
 * @returns An newly created extender.
 */
export function createExtenderEmail(): PrimeFaces.widget.Tagify.TagifyExtender {
  return {
    afterRender(tagify) {
      tagify.getTagify().on("input", (event) => {
        // Otherwise, we are editing an existing tag
        if ("value" in event.detail) {
          const value = event.detail.value;
          const result = split(value, " ,;", true);
          const endsOnDelimiter = value.endsWith(" ") || value.endsWith(",") || value.endsWith(";");
          if (
            result.split.length > 0 &&
            (result.split.length > 1 || (!result.incompletePlaceholder && endsOnDelimiter))
          ) {
            tagify.getTagify().DOM.input.textContent = "";
            for (const tag of result.split) {
              tagify.addTag(tag);
            }
          }
        }
      });
    },
    beforeRender(_tagify, cfg) {
      // Never let tagify split the input, we will add the code for splitting ourself
      cfg.delimiters = ";$x^";
    },
  };
}
